import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import WhatsAppMessageModal from '../../components/common/WhatsAppMessageModal';
import CollapsibleFilterPanel, { FilterField, filterInputProps } from '../../components/common/CollapsibleFilterPanel';
import { productAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { formatFeedbackLabel } from '../../utils/whatsappMessage';
import { buildOrderFormLink, copyOrderFormLink } from '../../utils/orderFormLink';
import { countActiveDealFilters, filterDeals, formatDateTimeIST } from '../../utils/dealFilters';

const extractDriveFileId = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value);

    if (!url.hostname.includes('drive.google.com')) {
      return '';
    }

    const filePathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
    return filePathMatch?.[1] || url.searchParams.get('id') || '';
  } catch (error) {
    return '';
  }
};

const resolveImageUrl = (value) => {
  if (!value) return '';

  const fileId = extractDriveFileId(value);
  if (fileId) {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
  }

  return value;
};

const resolveImageDownloadUrl = (value) => {
  if (!value) return '';

  const fileId = extractDriveFileId(value);
  if (fileId) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }

  return value;
};

const buildProductFeedbacks = (product) =>
  (product.feedbackConfigs || [])
    .filter((config) => Number(config?.remainingSlots || 0) > 0)
    .map((config) => ({
      feedbackType: config.feedbackType,
      payoutType: config.payoutType,
      payoutValue: config.payoutValue
    }));

const AvailableDeals = () => {
  const { mediatorId } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    productCode: '',
    productName: '',
    brandName: '',
    creationDate: '',
  });
  const [copiedId, setCopiedId] = useState(null);
  const [copiedOrderFormId, setCopiedOrderFormId] = useState(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageType, setMessageType] = useState('order');
  const [messageProduct, setMessageProduct] = useState(null);
  const [messageFeedbacks, setMessageFeedbacks] = useState([]);
  const [orderFormLink, setOrderFormLink] = useState('');

  const resolveMediatorId = (medId) =>
    (medId && typeof medId !== 'object') ? medId : (mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id);

  const handleCopyLink = async (link, productId) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(productId);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleViewImage = (imageUrl) => {
    const resolvedUrl = resolveImageUrl(imageUrl);
    if (!resolvedUrl) {
      toast.error('No image available');
      return;
    }

    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadImage = (imageUrl, fileName) => {
    const resolvedUrl = resolveImageDownloadUrl(imageUrl);
    if (!resolvedUrl) {
      toast.error('No image available');
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = resolvedUrl;
    anchor.download = fileName || 'product-image';
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const fetchDeals = async (medId = null) => {
    const effectiveMedId = resolveMediatorId(medId);
    if (!effectiveMedId) return;

    setLoading(true);
    try {
      const response = await productAPI.getMediatorAvailableProducts(effectiveMedId);
      let productsData = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        productsData = response.data.data;
      } else if (Array.isArray(response.data?.data)) {
        productsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        productsData = response.data;
      }

      setAllProducts(
        productsData.filter((product) => {
          const hasOpenSlots = (product.feedbackConfigs || []).some(
            (config) => Number(config?.remainingSlots || 0) > 0
          );
          return product.active !== false && hasOpenSlots;
        })
      );
    } catch (error) {
      console.error('Failed to fetch available deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialMedId = resolveMediatorId();
    if (initialMedId) {
      fetchDeals(initialMedId);
    }
  }, []);

  useEffect(() => {
    if (mediatorId) {
      fetchDeals(mediatorId);
    }
  }, [mediatorId]);

  const products = useMemo(() => filterDeals(allProducts, filters), [allProducts, filters]);
  const activeFilterCount = useMemo(() => countActiveDealFilters(filters), [filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({ productCode: '', productName: '', brandName: '', creationDate: '' });
  };

  const openMessageModal = (product, type = 'order') => {
    const medId = resolveMediatorId();
    const productId = product.id || product.productId;
    setMessageProduct(product);
    setMessageFeedbacks(buildProductFeedbacks(product));
    setMessageType(type);
    setOrderFormLink(buildOrderFormLink(productId, medId));
    setMessageModalOpen(true);
  };

  const handleCopyOrderFormLink = async (product) => {
    const medId = resolveMediatorId();
    const productId = product.id || product.productId;
    if (!medId || !productId) {
      toast.error('Could not build order form link');
      return;
    }
    try {
      await copyOrderFormLink(productId, medId);
      setCopiedOrderFormId(productId);
      toast.success('Order form link copied!');
      setTimeout(() => setCopiedOrderFormId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy order form link');
    }
  };

  const handleOpenOrderForm = (product) => {
    const medId = resolveMediatorId();
    const productId = product.id || product.productId;
    const link = buildOrderFormLink(productId, medId);
    if (!link) {
      toast.error('Could not build order form link');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: '0 0 4px 0' }}>Available Deals</h1>
            <p style={{ fontSize: '14px', color: '#64748b' }}>All active deals with open slots. Restricted deals appear only if assigned to you.</p>
          </div>
          <Button variant="secondary" onClick={() => fetchDeals()} disabled={loading}>
            <Icon name="refresh-cw" size={16} /> Refresh
          </Button>
        </div>

        <CollapsibleFilterPanel
          title="Search Deals"
          activeCount={activeFilterCount}
          onReset={resetFilters}
          gridColumns={4}
        >
          <FilterField label="Product Code">
            <input {...filterInputProps} value={filters.productCode} onChange={(e) => handleFilterChange('productCode', e.target.value)} placeholder="Search code" />
          </FilterField>
          <FilterField label="Product Name">
            <input {...filterInputProps} value={filters.productName} onChange={(e) => handleFilterChange('productName', e.target.value)} placeholder="Search name" />
          </FilterField>
          <FilterField label="Brand">
            <input {...filterInputProps} value={filters.brandName} onChange={(e) => handleFilterChange('brandName', e.target.value)} placeholder="Search brand" />
          </FilterField>
          <FilterField label="Created Date (IST)">
            <input {...filterInputProps} type="date" value={filters.creationDate} onChange={(e) => handleFilterChange('creationDate', e.target.value)} />
          </FilterField>
        </CollapsibleFilterPanel>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', marginTop: '20px' }}>
          {loading ? (
            <div style={{ gridColumn: '1 / -1', padding: '100px', textAlign: 'center', color: '#64748b' }}>
              <Icon name="refresh-cw" size={32} className="animate-spin" style={{ marginBottom: '16px' }} />
              <p>Loading active deals...</p>
            </div>
          ) : products.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', padding: '100px', textAlign: 'center', background: '#fff', borderRadius: '16px', border: '1px dashed #e2e8f0' }}>
              <Icon name="package" size={48} color="#e2e8f0" style={{ marginBottom: '16px' }} />
              <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>No active deals currently available.</p>
            </div>
          ) : (
            products.map((product) => (
              <div key={product.id || product.productId} style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ width: '84px', height: '84px', borderRadius: '10px', overflow: 'hidden', background: '#f1f5f9', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                      {product.imageUrl ? <img src={resolveImageUrl(product.imageUrl)} alt={product.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={18} /></div>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#667eea', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{product.brandName}</span>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '4px 0' }}>{product.productName}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 8px', background: '#f1f5f9', color: '#475569', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>{product.platformName}</span>
                        {product.productCode && <span style={{ fontSize: '11px', color: '#94a3b8' }}>#{product.productCode}</span>}
                      </div>
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#475569' }}>Product Amount: ₹{product.productAmount ?? '-'}</div>
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>Created: {formatDateTimeIST(product.creationDate)}</div>
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>
                        {product.restrictedToAssignedMediators === false
                          ? 'Open deal — any mediator can take this.'
                          : 'Assigned deal — visible because you are assigned to this product.'}
                      </div>
                      {product.imageUrl && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                          <button onClick={() => handleViewImage(product.imageUrl)} style={{ padding: '6px 10px', background: '#f8fafc', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Icon name="eye" size={14} /> View Image
                          </button>
                          <button onClick={() => handleDownloadImage(product.imageUrl, `${product.productCode || product.productName || 'product-image'}.jpg`)} style={{ padding: '6px 10px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Icon name="download" size={14} /> Download Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {(product.feedbackConfigs || []).filter((config) => config.remainingSlots > 0).map((config) => (
                      <div key={config.id || config.feedbackType} style={{ display: 'flex', gap: '12px', padding: '12px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                          {config.imageUrl ? <img src={resolveImageUrl(config.imageUrl)} alt={config.feedbackType} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={14} /></div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#1e293b' }}>{formatFeedbackLabel(config.feedbackType)}</div>
                          <div style={{ marginTop: '4px', fontSize: '13px', color: '#475569' }}>{config.payoutType}: ₹{config.payoutValue}</div>
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>Slots: {config.remainingSlots}/{config.totalSlots}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => openMessageModal(product, 'order')}
                    style={{ width: '100%', marginBottom: '8px', padding: '10px 12px', background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    <Icon name="message-circle" size={16} /> Order Message
                  </button>
                  <button
                    onClick={() => openMessageModal(product, 'announcement')}
                    style={{ width: '100%', marginBottom: '8px', padding: '10px 12px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    <Icon name="deals" size={16} /> Deal Announcement
                  </button>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      onClick={() => handleCopyOrderFormLink(product)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: copiedOrderFormId === (product.id || product.productId) ? '#ecfdf5' : '#eff6ff',
                        color: copiedOrderFormId === (product.id || product.productId) ? '#059669' : '#2563eb',
                        border: `1px solid ${copiedOrderFormId === (product.id || product.productId) ? '#bbf7d0' : '#bfdbfe'}`,
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <Icon name={copiedOrderFormId === (product.id || product.productId) ? 'tick' : 'cp'} size={14} />
                      {copiedOrderFormId === (product.id || product.productId) ? 'Copied!' : 'Copy Order Form Link'}
                    </button>
                    <button
                      onClick={() => handleOpenOrderForm(product)}
                      style={{ flex: 1, padding: '8px 12px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <Icon name="external-link" size={14} /> Open Order Form
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    {product.link && (
                      <>
                        <button onClick={() => window.open(product.link, '_blank')} style={{ flex: 1, padding: '8px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <Icon name="external-link" size={14} /> View Product
                        </button>
                        <button onClick={() => handleCopyLink(product.link, product.id || product.productId)} style={{ flex: 1, padding: '8px 12px', background: copiedId === (product.id || product.productId) ? '#f0fdf4' : '#f8fafc', color: copiedId === (product.id || product.productId) ? '#16a34a' : '#475569', border: `1px solid ${copiedId === (product.id || product.productId) ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <Icon name={copiedId === (product.id || product.productId) ? 'check' : 'copy'} size={14} /> {copiedId === (product.id || product.productId) ? 'Copied!' : 'Copy Link'}
                        </button>
                      </>
                    )}
                  </div>

                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <WhatsAppMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        product={messageProduct}
        feedbacks={messageFeedbacks}
        orderFormLink={orderFormLink}
        messageType={messageType}
      />
    </div>
  );
};

export default AvailableDeals;
